// ============================================================================
// HIRING PAGE - Team > Hiring
// ============================================================================
// Manages hiring candidates and interview tracking.
// Role-based: admin + supervisor for current; admin-only writes for archived.
// Styled to match TeamMemberFullScreenPage.jsx (BRIO inline-style system).
//
// ── App.js INTEGRATION NOTE (Part B) ───────────────────────────────────
// Where HiringPage is rendered in App.js, pass activeTeams + settingsHook:
//   <HiringPage
//     currentUser={currentUser}
//     activeUsers={activeUsers}
//     activeProjects={activeProjects}
//     activeTeams={activeTeams}       // ← ADD THIS PROP
//     useSupabaseDB={useSupabaseDB}
//     hiringHook={hiringHook}
//     settingsHook={settingsHook}     // ← ADD THIS PROP (from useSettings())
//   />
// ────────────────────────────────────────────────────────────────────────
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Plus, Trash2, ChevronDown, ChevronRight, Copy, ExternalLink,
  X, RotateCcw, UserCheck, UserX, UserPlus, Mail,
  AlertTriangle, Check, Loader2, Save, Users, Briefcase, GraduationCap,
  Search, SlidersHorizontal, Star, Circle, PanelLeftClose, PanelLeftOpen
} from 'lucide-react'; // eslint-disable-line no-unused-vars
import { BrioDatePicker, BrioModal, BrioModalBody, BrioModalField, BrioBadge } from '../../components/BrioComponents';
import EmailDraftModal from '../../components/EmailDraftModal';
import useCommunicationLog from '../../hooks/useCommunicationLog';
import {
  renderEmailTemplate,
  buildMailtoUrl,
  getHiringTemplateTypes,
} from '../../utils/emailTemplates';

// ============================================================================
// STYLE CONSTANTS — BRIO Design System (copied from TeamMemberFullScreenPage)
// ============================================================================
const BRIO = {
  gold: 'var(--brio-gold)',
  goldHover: 'var(--brio-gold-hover)',
  goldLight: 'var(--brio-gold-light)',
  goldMedium: 'rgba(159, 144, 42, 0.2)',
  white: 'var(--brio-white)',
  black: 'var(--brio-black)',
  grey: 'var(--brio-grey)',
  greyLight: 'var(--brio-grey-light)',
  greyMuted: 'var(--brio-grey)',
  border: 'var(--brio-border)',
  borderStrong: 'rgba(0, 0, 0, 0.15)',
  danger: 'var(--brio-red)',
  dangerBg: 'rgba(147, 73, 72, 0.08)',
  success: 'var(--brio-green)',
  successBg: 'rgba(132, 164, 140, 0.08)',
  warningBg: 'rgba(236, 210, 136, 0.15)',
  infoBg: 'rgba(104, 149, 129, 0.08)',
  font: "'Raleway', sans-serif",
};

const INPUT_STYLE = {
  width: '100%',
  padding: '7px 10px',
  border: `1px solid ${BRIO.border}`,
  borderRadius: 0,
  fontSize: '13px',
  lineHeight: '1.4',
  background: BRIO.white,
  color: BRIO.black,
  boxSizing: 'border-box',
  fontFamily: BRIO.font,
  transition: 'border-color 0.15s',
};

const SELECT_STYLE = { ...INPUT_STYLE, appearance: 'auto' };

const LABEL_STYLE = {
  fontSize: '11px',
  fontWeight: 500,
  color: BRIO.grey,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '4px',
  display: 'block',
  fontFamily: BRIO.font,
};

const SECTION_LABEL_STYLE = {
  fontSize: '12px',
  fontWeight: 500,
  color: BRIO.black,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '10px',
  marginTop: '20px',
  paddingBottom: '6px',
  borderBottom: `1px solid ${BRIO.border}`,
  fontFamily: BRIO.font,
};

// ============================================================================
// AUTO-RESIZE TEXTAREA — grows vertically to fit content, no scrollbar
// ============================================================================
function AutoResizeTextarea({ value, onChange, onBlur, disabled, placeholder, minRows = 2, style: styleProp, ...rest }) {
  const ref = useRef(null);
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, []);

  // Resize on value changes (typing, programmatic updates, data rehydration)
  useEffect(() => { resize(); }, [value, resize]);

  // Resize once after initial mount (pre-filled text)
  useEffect(() => {
    const id = requestAnimationFrame(resize);
    return () => cancelAnimationFrame(id);
  }, [resize]);

  const mergedStyle = {
    ...INPUT_STYLE,
    resize: 'none',
    overflow: 'hidden',
    minHeight: `${minRows * 1.4 * 13 + 14}px`,
    ...styleProp,
  };

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onInput={resize}
      disabled={disabled}
      placeholder={placeholder}
      rows={minRows}
      style={mergedStyle}
      {...rest}
    />
  );
}

// ============================================================================
// CONSTANTS (defined locally to avoid transitive import chain to supabaseClient)
// Keep in sync with useSupabaseHiring.js
// ============================================================================
// HIRING_CATEGORIES is now a FALLBACK DEFAULT only.
// The live list comes from settingsHook.candidateRoles (app_settings).
const DEFAULT_HIRING_CATEGORIES = [
  'Architecture',
  'Interior Designer',
  'Model Maker',
  'Graphic Designer',
  'HR',
  'Admin',
  'PR'
];

// Pseudo-category shown as the first pill — not a real candidate category,
// it just clears the category filter so every candidate is listed.
const ALL_CATEGORY = 'All';

const SOFTWARE_PROFICIENCY_KEYS = [
  { key: 'adobe', label: 'Adobe Suite' },
  { key: 'autocad', label: 'AutoCAD' },
  { key: 'revit', label: 'Revit' },
  { key: 'rhino', label: 'Rhino' },
  { key: 'sketchup', label: 'SketchUp' },
  { key: 'enscape', label: 'Enscape' },
  { key: 'lumion', label: 'Lumion' },
  { key: 'ai_image_generating', label: 'AI Image Gen' },
  { key: 'coding', label: 'Coding' }
];


// UUID validation helpers — interview_taken_by is UUID in Supabase
const isUuid = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const getUserUuid = (u) => {
  const candidates = [
    u?.user_id, u?.auth_user_id, u?.authUserId,
    u?.profile_id, u?.profileId,
    u?.id
  ];
  return candidates.find(isUuid) || null;
};

const ARCHIVE_REASONS = ['Hired', 'Rejected', 'Future potential'];

const BRIO_JOB_ROLES = [
  'Intern', 'Junior Architect', 'Medior Architect', 'Project Architect',
  'Senior Architect', 'Associate', 'Partner', 'Director', 'Support',
  'Model Maker', 'Visualiser', 'Others',
];


// ============================================================================
// SUB-COMPONENTS — BRIO styled (matches TeamMemberFullScreenPage patterns)
// ============================================================================

function Field({ label, children, span2 }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: '2px',
      gridColumn: span2 ? 'span 2' : undefined,
    }}>
      <label style={LABEL_STYLE}>{label}</label>
      {children}
    </div>
  );
}

function FieldGrid({ children, cols = 2 }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gap: '14px 20px',
      padding: '16px 0',
    }}>
      {children}
    </div>
  );
}

function Toast({ message, type, onDismiss }) {
  if (!message) return null;
  const bg = type === 'error' ? BRIO.dangerBg : BRIO.successBg;
  const color = type === 'error' ? 'var(--brio-red)' : 'var(--brio-green)';
  const border = type === 'error' ? '#fecaca' : '#86efac';
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 100002,
      padding: '12px 20px', background: bg, color,
      border: `1px solid ${border}`, borderRadius: 'var(--brio-radius-modal)',
      fontSize: '13px', fontFamily: BRIO.font,
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '400px',
    }}>
      {type === 'error'
        ? <AlertTriangle style={{ width: 14, height: 14 }} />
        : <Check style={{ width: 14, height: 14 }} />}
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onDismiss} style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color
      }}>
        <X style={{ width: 12, height: 12 }} />
      </button>
    </div>
  );
}

function ModalOverlay({ title, onClose, children }) {
  return (
    <BrioModal title={title} onClose={onClose} width="md">
      {children}
    </BrioModal>
  );
}


// ============================================================================
// HELPER: Proficiency tick rating component (1/2/3 or null)
// ============================================================================
const ProficiencyRating = ({ value, onChange, disabled }) => {
  const levels = [1, 2, 3];
  const labels = ['Beginner', 'Experienced', 'Expert'];
  const colors = {
    1: { bg: 'rgba(236, 210, 136, 0.15)', border: '#fcd34d', text: 'var(--brio-orange)' },
    2: { bg: 'rgba(104, 149, 129, 0.08)', border: '#93c5fd', text: '#1e40af' },
    3: { bg: 'rgba(132, 164, 140, 0.08)', border: '#86efac', text: 'var(--brio-green)' },
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
      {levels.map((level) => {
        const active = value === level;
        const c = colors[level];
        return (
          <button
            key={level}
            type="button"
            disabled={disabled}
            onClick={() => onChange(value === level ? null : level)}
            title={labels[level - 1]}
            style={{
              width: 24, height: 24, borderRadius: 0,
              fontSize: '10px', fontWeight: 700, lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: active ? c.bg : BRIO.white,
              border: `1px solid ${active ? c.border : BRIO.border}`,
              color: active ? c.text : BRIO.greyMuted,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              transition: 'all 0.15s',
              fontFamily: BRIO.font,
            }}
          >
            {level === 1 ? '✓' : level === 2 ? '✓✓' : '★'}
          </button>
        );
      })}
    </div>
  );
};

// ============================================================================
// HELPER: Past Employment Chips
// ============================================================================
const PastEmploymentEditor = ({ items = [], onChange, disabled }) => {
  const [draft, setDraft] = useState('');
  const handleAdd = () => {
    const trimmed = draft.trim();
    if (trimmed && !items.includes(trimmed)) {
      onChange([...items, trimmed]);
      setDraft('');
    }
  };
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
        {items.map((item, idx) => (
          <span key={idx} style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '2px 8px', background: BRIO.greyLight,
            color: BRIO.black, borderRadius: 0, fontSize: '11px',
            fontFamily: BRIO.font,
          }}>
            {item}
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange(items.filter((_, i) => i !== idx))}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: BRIO.greyMuted, padding: '0 1px', lineHeight: 1,
                }}
              >
                <X style={{ width: 10, height: 10 }} />
              </button>
            )}
          </span>
        ))}
        {items.length === 0 && (
          <span style={{ fontSize: '11px', color: BRIO.greyMuted, fontStyle: 'italic' }}>
            None added
          </span>
        )}
      </div>
      {!disabled && (
        <div style={{ display: 'flex', gap: '4px' }}>
          <input
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder="Add employer..."
            style={{ ...INPUT_STYLE, flex: 1, padding: '4px 8px', fontSize: '12px' }}
          />
          <button type="button" onClick={handleAdd} style={{
            padding: '4px 10px', fontSize: '11px',
            background: BRIO.greyLight, border: `1px solid ${BRIO.border}`,
            borderRadius: 0, cursor: 'pointer', fontFamily: BRIO.font,
            color: BRIO.grey,
          }}>
            Add
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// HELPER: Degrees / Education Editor (table-like rows)
// ============================================================================
const DEGREE_TYPES = [
  { value: 'bachelor', label: 'Bachelor' },
  { value: 'master', label: 'Master' },
  { value: 'phd', label: 'PhD' },
  { value: 'diploma', label: 'Diploma' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'other', label: 'Other' },
];

// Normalize legacy Title Case degree types ("Bachelor") → lowercase ("bachelor")
const normalizeDegreeType = (t) => {
  if (!t) return '';
  const lower = t.toLowerCase();
  return DEGREE_TYPES.find(d => d.value === lower) ? lower : t;
};
const normalizeDegrees = (degrees) =>
  (degrees || []).map(d => ({ ...d, type: normalizeDegreeType(d.type) }));

const DegreesEditor = ({ items = [], onChange, disabled }) => {
  const handleAdd = () => {
    onChange([...items, { type: '', institution: '', graduationDate: '', duration: '', notes: '' }]);
  };
  const handleUpdate = (idx, field, value) => {
    const updated = items.map((d, i) => i === idx ? { ...d, [field]: value } : d);
    onChange(updated);
  };
  const handleRemove = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  return (
    <div>
      {items.length === 0 && (
        <div style={{ fontSize: '11px', color: BRIO.greyMuted, fontStyle: 'italic', marginBottom: '8px' }}>
          No degrees added
        </div>
      )}
      {items.map((deg, idx) => (
        <div key={idx} style={{
          display: 'grid', gridTemplateColumns: '120px 1fr 110px 80px 1fr auto',
          gap: '6px', alignItems: 'start', padding: '8px 0',
          borderBottom: idx < items.length - 1 ? `1px solid ${BRIO.border}` : 'none',
        }}>
          <select value={deg.type || ''} onChange={e => handleUpdate(idx, 'type', e.target.value)} disabled={disabled}
            style={{ ...SELECT_STYLE, padding: '5px 6px', fontSize: '12px' }}>
            <option value="">Type…</option>
            {DEGREE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input type="text" value={deg.institution || ''} onChange={e => handleUpdate(idx, 'institution', e.target.value)} disabled={disabled}
            style={{ ...INPUT_STYLE, padding: '5px 8px', fontSize: '12px' }} placeholder="Institution" />
          <BrioDatePicker value={deg.graduationDate || ''} onChange={v => handleUpdate(idx, 'graduationDate', v)} disabled={disabled}
            placeholder="Graduation date" />
          <input type="text" value={deg.duration || ''} onChange={e => handleUpdate(idx, 'duration', e.target.value)} disabled={disabled}
            style={{ ...INPUT_STYLE, padding: '5px 6px', fontSize: '12px' }} placeholder="e.g. 4yr" />
          <input type="text" value={deg.notes || ''} onChange={e => handleUpdate(idx, 'notes', e.target.value)} disabled={disabled}
            style={{ ...INPUT_STYLE, padding: '5px 8px', fontSize: '12px' }} placeholder="Notes" />
          {!disabled && (
            <button type="button" onClick={() => handleRemove(idx)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO.greyMuted, padding: '6px 2px' }}>
              <Trash2 style={{ width: 12, height: 12 }} />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button type="button" onClick={handleAdd}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '6px',
            padding: '4px 10px', fontSize: '11px', background: BRIO.greyLight,
            border: `1px solid ${BRIO.border}`, borderRadius: 0,
            cursor: 'pointer', fontFamily: BRIO.font, color: BRIO.grey,
          }}>
          <Plus style={{ width: 11, height: 11 }} /> Add Degree
        </button>
      )}
    </div>
  );
};

// ============================================================================
// HELPER: Multi-select dropdown for suitable projects
// ============================================================================
const ProjectMultiSelect = ({ selectedIds = [], projects = [], onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (projId) => {
    if (disabled) return;
    const exists = selectedIds.includes(projId);
    onChange(exists ? selectedIds.filter(id => id !== projId) : [...selectedIds, projId]);
  };

  const sortedProjects = [...projects].sort((a, b) => (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || ''));
  const selectedNames = projects.filter(p => selectedIds.includes(p.id)).map(p => p.projectCode ? `${p.projectCode} \u2014 ${p.name}` : p.name);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        style={{
          ...INPUT_STYLE,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 10px', fontSize: '12px', textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: disabled ? BRIO.greyLight : BRIO.white,
        }}
      >
        <span style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          color: selectedNames.length > 0 ? BRIO.black : BRIO.greyMuted,
        }}>
          {selectedNames.length > 0 ? selectedNames.join(', ') : 'Select projects...'}
        </span>
        <ChevronDown style={{ width: 12, height: 12, color: BRIO.greyMuted, flexShrink: 0, marginLeft: '4px' }} />
      </button>
      {open && !disabled && (
        <div style={{
          position: 'absolute', zIndex: 50, marginTop: '2px', width: '100%',
          maxHeight: '200px', overflowY: 'auto', background: BRIO.white,
          border: `1px solid ${BRIO.border}`, borderRadius: 0,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          {projects.length === 0 && (
            <div style={{ padding: '8px 12px', fontSize: '11px', color: BRIO.greyMuted }}>
              No projects
            </div>
          )}
          {sortedProjects.map(p => (
            <label key={p.id} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '6px 12px', cursor: 'pointer', fontSize: '12px',
              fontFamily: BRIO.font, color: BRIO.black,
              borderBottom: `1px solid ${BRIO.border}`,
            }}>
              <input
                type="checkbox"
                checked={selectedIds.includes(p.id)}
                onChange={() => toggle(p.id)}
                style={{ accentColor: BRIO.gold }}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.projectCode ? `${p.projectCode} \u2014 ${p.name}` : p.name}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};


// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function HiringPage({
  currentUser,
  activeUsers = [],
  activeProjects = [],
  activeTeams = [],
  useSupabaseDB,
  hiringHook,
  settingsHook,
  customEmailTemplates,
  pendingHiringAction,
  clearPendingHiringAction,
}) {
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'admin-pa';
  const isSupervisor = currentUser?.role === 'supervisor';
  // Admin-granted temporary 'hiring' page access — scoped like supervisor:
  // current candidates only, write enabled, no delete, no archived/hired view.
  const hasHiringTempAccess = (() => {
    const pages = currentUser?.tempAccessPages;
    if (!Array.isArray(pages) || !pages.includes('hiring')) return false;
    const expiresAt = currentUser?.tempAccessExpiresAt;
    if (!expiresAt) return false;
    const t = new Date(expiresAt).getTime();
    return Number.isFinite(t) && t > Date.now();
  })();
  const currentOnlyView = isSupervisor || (!isAdmin && hasHiringTempAccess);
  const canWrite = isAdmin || isSupervisor || hasHiringTempAccess;

  // Dynamic hiring categories from Settings (falls back to hardcoded defaults)
  const HIRING_CATEGORIES = useMemo(() => {
    const fromSettings = settingsHook?.candidateRoles;
    // Drop any literal 'All' coming from Settings — it would duplicate the pseudo-category pill
    const isAllLabel = (c) => String(c).trim().toLowerCase() === ALL_CATEGORY.toLowerCase();
    if (Array.isArray(fromSettings) && fromSettings.length > 0) return fromSettings.filter(c => !isAllLabel(c));
    return DEFAULT_HIRING_CATEGORIES;
  }, [settingsHook?.candidateRoles]);

  // Pills = 'All' + the real categories
  const CATEGORY_PILLS = useMemo(() => [ALL_CATEGORY, ...HIRING_CATEGORIES], [HIRING_CATEGORIES]);

  // ------------------------------------------------------------------
  // STATE
  // ------------------------------------------------------------------
  const [viewMode, setViewMode] = useState('current');
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORY);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [expandedInterviews, setExpandedInterviews] = useState({});
  const [showNewCandidateModal, setShowNewCandidateModal] = useState(false);
  const [newCandidateForm, setNewCandidateForm] = useState({ candidateName: '', category: DEFAULT_HIRING_CATEGORIES[0], jobRole: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const [emailDraftAction, setEmailDraftAction] = useState(null); // { eventType, archiveReason? }
  const [contactTemplateKey, setContactTemplateKey] = useState('hiring_contact');
  const { logEmail } = useCommunicationLog();
  const actionDropdownRef = useRef(null);

  // 'All' is a filter, not a real category — the new-candidate form needs a real one.
  const isAllCategory = selectedCategory === ALL_CATEGORY;
  const defaultNewCategory = isAllCategory
    ? (HIRING_CATEGORIES[0] || DEFAULT_HIRING_CATEGORIES[0])
    : selectedCategory;

  // Close action dropdown when clicking outside
  useEffect(() => {
    if (!actionDropdownOpen) return;
    const handleClick = (e) => {
      if (actionDropdownRef.current && !actionDropdownRef.current.contains(e.target)) {
        setActionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [actionDropdownOpen]);

  // Sync selectedCategory when HIRING_CATEGORIES changes from settings
  // (e.g., admin deletes the currently selected role)
  useEffect(() => {
    if (selectedCategory === ALL_CATEGORY) return;
    if (HIRING_CATEGORIES.length > 0 && !HIRING_CATEGORIES.includes(selectedCategory)) {
      setSelectedCategory(ALL_CATEGORY);
    }
  }, [HIRING_CATEGORIES]); // eslint-disable-line react-hooks/exhaustive-deps


  const canWriteInCurrentMode = viewMode === 'current' ? canWrite : viewMode === 'archived' ? isAdmin : viewMode === 'hired' ? isAdmin : false;
  const canDeleteInCurrentMode = viewMode === 'current' ? isAdmin : viewMode === 'archived' ? isAdmin : viewMode === 'hired' ? isAdmin : false;

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    if (type === 'success') setTimeout(() => setToast(null), 3000);
  }, []);

  // ------------------------------------------------------------------
  // DATA FROM HOOK
  // ------------------------------------------------------------------
  const {
    loading,
    listCandidates,
    createCandidate,
    updateCandidate,
    deleteCandidate,
    createInterview,
    updateInterview,
    deleteInterview,
    getInterviewsForCandidate,
    hireCandidate,
    refetch // eslint-disable-line no-unused-vars
  } = hiringHook;

  // Hire modal state (admin only)
  const [showHireModal, setShowHireModal] = useState(false);
  const HIRE_FORM_DEFAULTS = { workEmail: '', teamId: '', jobRole: '', jobLevel: '', joinDate: '', role: 'user', contractedHours: '176', officeLocation: 'NL', nlSchaal: '', nlTrede: '' };
  const [hireForm, setHireForm] = useState(HIRE_FORM_DEFAULTS);

  const isArchived = viewMode === 'archived';
  const isHired = viewMode === 'hired';
  const filteredCandidates = useMemo(
    // 'All' → omit the category filter entirely
    () => listCandidates({ viewMode, category: selectedCategory === ALL_CATEGORY ? undefined : selectedCategory }),
    [listCandidates, viewMode, selectedCategory]
  );

  const selectedCandidate = useMemo(
    () => filteredCandidates.find(c => c.id === selectedCandidateId) || null,
    [filteredCandidates, selectedCandidateId]
  );

  const candidateInterviews = useMemo(
    () => selectedCandidateId ? getInterviewsForCandidate(selectedCandidateId) : [],
    [getInterviewsForCandidate, selectedCandidateId]
  );

  // ------------------------------------------------------------------
  // CANDIDATE LIST PANEL — search, filter, sort, pagination, resize
  // ------------------------------------------------------------------
  const [listSearch, setListSearch] = useState('');
  const [listFilter, setListFilter] = useState('all');
  const [listSort, setListSort] = useState('activity');
  const PAGE_SIZE = 30;
  const [listShowCount, setListShowCount] = useState(PAGE_SIZE);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [panelWidth, setPanelWidth] = useState(310);
  const resizing = useRef(false);
  const panelRef = useRef(null);

  // Drag-to-resize handler
  const onResizeStart = useCallback((e) => {
    e.preventDefault();
    resizing.current = true;
    const startX = e.clientX;
    const startW = panelWidth;
    const onMove = (ev) => {
      if (!resizing.current) return;
      const delta = ev.clientX - startX;
      const next = Math.max(220, Math.min(520, startW + delta));
      setPanelWidth(next);
    };
    const onUp = () => {
      resizing.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [panelWidth]);

  // Reset visible count when category / viewMode / search / filter change
  useEffect(() => { setListShowCount(PAGE_SIZE); }, [selectedCategory, viewMode, listSearch, listFilter]);

  // Enrich candidates with interview-derived data
  const enrichedCandidates = useMemo(() => {
    return filteredCandidates.map(c => {
      const interviews = getInterviewsForCandidate(c.id);
      const lastInterviewDate = interviews.reduce((max, i) => {
        if (!i.interviewDate) return max;
        return (!max || i.interviewDate > max) ? i.interviewDate : max;
      }, null);
      const latestByIndex = interviews.length > 0
        ? interviews.reduce((a, b) => (a.interviewIndex > b.interviewIndex ? a : b))
        : null;
      // Tri-state: true = yes, null = ?, false = no
      const latestRec = latestByIndex?.recommended;
      const isRecommended = latestRec === true;
      const isUndecided = latestRec === null && latestByIndex != null;
      const interviewCount = interviews.length;
      const candTs = c.updatedAt || c.createdAt || '';
      const intTs = latestByIndex?.updatedAt || latestByIndex?.createdAt || '';
      const lastActivity = candTs > intTs ? candTs : intTs;
      return {
        ...c,
        _lastInterviewDate: lastInterviewDate,
        _isRecommended: isRecommended,
        _isUndecided: isUndecided,
        _interviewCount: interviewCount,
        _lastActivity: lastActivity,
      };
    });
  }, [filteredCandidates, getInterviewsForCandidate]);

  // Apply search + filter + sort
  const processedCandidates = useMemo(() => {
    let list = enrichedCandidates;
    // Search by name or city
    if (listSearch.trim()) {
      const q = listSearch.trim().toLowerCase();
      list = list.filter(c =>
        (c.candidateName || '').toLowerCase().includes(q) ||
        (c.locationCity || '').toLowerCase().includes(q)
      );
    }
    // Filter chips
    if (listFilter === 'recommended') list = list.filter(c => c._isRecommended);
    else if (listFilter === 'interviewed') list = list.filter(c => c._interviewCount > 0);
    else if (listFilter === 'no_interview') list = list.filter(c => c._interviewCount === 0);
    else if (listFilter === 'Hired') list = list.filter(c => c.archiveReason === 'Hired');
    else if (listFilter === 'Rejected') list = list.filter(c => c.archiveReason === 'Rejected');
    else if (listFilter === 'Future potential') list = list.filter(c => c.archiveReason === 'Future potential');
    // Sort
    list = [...list].sort((a, b) => {
      switch (listSort) {
        case 'name': return (a.candidateName || '').localeCompare(b.candidateName || '');
        case 'experience': return (b.yearsExperience || 0) - (a.yearsExperience || 0);
        case 'joining': return (a.possibleJoiningDate || '9999').localeCompare(b.possibleJoiningDate || '9999');
        case 'activity':
        default: return (b._lastActivity || '').localeCompare(a._lastActivity || '');
      }
    });
    return list;
  }, [enrichedCandidates, listSearch, listFilter, listSort]);

  const visibleCandidates = useMemo(
    () => processedCandidates.slice(0, listShowCount),
    [processedCandidates, listShowCount]
  );
  const hasMore = processedCandidates.length > listShowCount;

  // Auto-select first candidate when list changes
  useEffect(() => {
    if (processedCandidates.length > 0) {
      const stillExists = processedCandidates.find(c => c.id === selectedCandidateId);
      if (!stillExists) {
        setSelectedCandidateId(processedCandidates[0].id);
      }
    } else {
      setSelectedCandidateId(null);
    }
  }, [processedCandidates, selectedCandidateId]);

  useEffect(() => {
    if (selectedCandidate) {
      setEditingCandidate({
        ...selectedCandidate,
        degrees: normalizeDegrees(selectedCandidate.degrees),
      });
    } else {
      setEditingCandidate(null);
    }
    setExpandedInterviews({});
  }, [selectedCandidateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Consume pendingHiringAction from RADAR navigation ──
  useEffect(() => {
    if (!pendingHiringAction || !processedCandidates.length) return;
    const { candidateId, pendingAction } = pendingHiringAction;
    if (!candidateId) return;
    // Switch to the correct view mode so the candidate is visible
    if (pendingAction === 'send_hire_letter' && !currentOnlyView) {
      setViewMode('hired');
    }
    // Find the candidate's category and select them
    const cand = processedCandidates.find(c => String(c.id) === String(candidateId));
    if (cand) {
      setSelectedCandidateId(cand.id);
      // Auto-trigger the action for admin
      if (isAdmin && pendingAction) {
        // Slight delay to let state settle before triggering action
        setTimeout(() => {
          if (pendingAction === 'offer') {
            setEmailDraftAction({ eventType: 'hiring_offer' });
          } else if (pendingAction === 'hire') {
            handleOpenHireModal();
          } else if (pendingAction === 'send_hire_letter') {
            setEmailDraftAction({ eventType: 'hiring_offer' });
          }
        }, 300);
      }
    }
    if (clearPendingHiringAction) clearPendingHiringAction();
  }, [pendingHiringAction, processedCandidates]); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------------------------------------------
  // HANDLERS (all logic preserved exactly)
  // ------------------------------------------------------------------
  const handleCreateCandidate = async () => {
    if (!newCandidateForm.candidateName.trim()) return;
    setSaving(true);
    const result = await createCandidate({
      candidateName: newCandidateForm.candidateName.trim(),
      category: newCandidateForm.category || defaultNewCategory,
      jobRole: newCandidateForm.jobRole || '',
      isArchived: false, employedStatus: 'not_employed',
      pastEmployment: [], yearsExperience: null,
      possibleJoiningDate: '', portfolioUrl: '', notes: ''
    });
    setSaving(false);
    if (result.success) {
      setShowNewCandidateModal(false);
      setNewCandidateForm({ candidateName: '', category: defaultNewCategory, jobRole: '' });
      if (result.data) {
        // Stay on 'All' if that's the active filter — the new candidate is visible there anyway
        if (!isAllCategory) setSelectedCategory(result.data.category);
        setSelectedCandidateId(result.data.id);
      }
      showToast('Candidate created');
    } else {
      showToast(result.error || 'Failed to create candidate', 'error');
    }
  };

  const handleSaveCandidate = async () => {
    if (!editingCandidate || !selectedCandidateId) return;
    if (!editingCandidate.jobRole) {
      showToast('Job Role is required', 'error');
      return;
    }
    setSaving(true);
    const result = await updateCandidate(selectedCandidateId, editingCandidate);
    setSaving(false);
    if (result.success) showToast('Candidate saved');
    else showToast(result.error || 'Failed to save', 'error');
  };

  const handleArchiveToggle = async (reason) => {
    if (!selectedCandidateId) return;
    const current = selectedCandidate;
    if (!current) return;
    setSaving(true);
    if (current.isArchived && !reason) {
      // Unarchive: merge cleared archive fields into the full candidate object
      const result = await updateCandidate(selectedCandidateId, {
        ...current,
        isArchived: false, archivedAt: null, archivedBy: null, archiveReason: null
      });
      setSaving(false);
      if (result.success) showToast('Candidate unarchived');
      else showToast(result.error || 'Failed to unarchive', 'error');
    } else if (reason) {
      // Archive: merge archive fields into the full candidate object
      // NOTE: archivedBy is set automatically in the hook via authUid (UUID),
      // NOT from currentUser.id (numeric app ID) — type mismatch would cause
      // "Invalid data format" errors from Postgres.
      const result = await updateCandidate(selectedCandidateId, {
        ...current,
        isArchived: true, archivedAt: new Date().toISOString(),
        archiveReason: reason
      });
      setSaving(false);
      if (result.success) showToast(`Candidate archived as "${reason}"`);
      else showToast(result.error || 'Failed to archive', 'error');
    }
    setSaving(false);
  };

  const handleMoveHiredToCurrent = async () => {
    if (!selectedCandidateId || !selectedCandidate) return;
    if (!window.confirm('Move this hired candidate back to current candidates? This will clear the hired status.')) return;
    setSaving(true);
    const result = await updateCandidate(selectedCandidateId, {
      ...selectedCandidate,
      hiredUserId: null,
      isArchived: false,
      archivedAt: null,
      archivedBy: null,
      archiveReason: null,
    });
    setSaving(false);
    if (result.success) showToast('Candidate moved back to current');
    else showToast(result.error || 'Failed to move candidate', 'error');
  };

  const handleAddInterview = async () => {
    if (!selectedCandidateId) return;
    const nextIndex = candidateInterviews.length > 0
      ? Math.max(...candidateInterviews.map(i => i.interviewIndex)) + 1 : 1;
    setSaving(true);
    const result = await createInterview(selectedCandidateId, {
      interviewIndex: nextIndex, interviewDate: '', overallComments: '',
      softwareProficiency: {}, salaryExpectationsMonthly: null,
      currentSalaryMonthly: null, futurePlans: '', interviewTakenBy: null,
      suitableProjectIds: [], finalVerdictNotes: '', recommended: false
    });
    setSaving(false);
    if (result.success && result.data) {
      setExpandedInterviews(prev => ({ ...prev, [result.data.id]: true }));
      showToast('Interview added');
    }
  };

  const handleUpdateInterview = async (interviewId, updates) => {
    setSaving(true);
    const result = await updateInterview(interviewId, updates);
    setSaving(false);
    if (result.success) showToast('Interview saved');
    else showToast(result.error || 'Failed to update interview', 'error');
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    setSaving(true);
    let result;
    if (confirmDelete.type === 'candidate') result = await deleteCandidate(confirmDelete.id);
    else result = await deleteInterview(confirmDelete.id);
    setSaving(false);
    setConfirmDelete(null);
    if (result.success) showToast('Deleted');
    else showToast(result.error || 'Failed to delete', 'error');
  };

  // ------------------------------------------------------------------
  // HIRE CANDIDATE (admin only)
  // ------------------------------------------------------------------
  const handleOpenHireModal = () => {
    if (!selectedCandidate) return;
    setHireForm({
      ...HIRE_FORM_DEFAULTS,
      joinDate: selectedCandidate.possibleJoiningDate || '',
      jobRole: selectedCandidate.jobRole || '',
    });
    setShowHireModal(true);
  };

  const handleConfirmHire = async () => {
    if (!selectedCandidateId) return;
    if (!hireForm.workEmail.trim()) { showToast('Work email is required', 'error'); return; }
    if (!hireForm.jobRole) { showToast('Job role is required', 'error'); return; }
    if (!hireForm.jobLevel && hireForm.jobLevel !== '0' && hireForm.jobLevel !== 0) { showToast('Job level is required', 'error'); return; }
    if (!hireForm.joinDate) { showToast('Join date is required', 'error'); return; }
    const isNL = (hireForm.officeLocation || 'NL') === 'NL';
    if (isNL && !hireForm.nlSchaal) { showToast('Schaal is required for NL office', 'error'); return; }
    if (isNL && !hireForm.nlTrede && hireForm.nlTrede !== '0' && hireForm.nlTrede !== 0) { showToast('Trede is required for NL office', 'error'); return; }
    setSaving(true);
    const result = await hireCandidate(selectedCandidateId, {
      workEmail: hireForm.workEmail.trim(),
      teamId: hireForm.teamId || null,
      jobRole: hireForm.jobRole,
      jobLevel: parseInt(hireForm.jobLevel, 10),
      joinDate: hireForm.joinDate,
      role: hireForm.role || 'user',
      contractedHours: hireForm.contractedHours ? parseFloat(hireForm.contractedHours) : 176,
      officeLocation: hireForm.officeLocation || 'NL',
      nlSchaal: isNL ? hireForm.nlSchaal : null,
      nlTrede: isNL ? parseInt(hireForm.nlTrede, 10) : null,
      // Candidate contact/address fields (passed through to Edge Function)
      phoneMobile: selectedCandidate.phoneMobile || null,
      phoneWhatsapp: selectedCandidate.phoneWhatsapp || null,
      phoneCustomLabel: selectedCandidate.phoneCustomLabel || null,
      phoneCustomValue: selectedCandidate.phoneCustomValue || null,
      addressLine1: selectedCandidate.addressLine1 || null,
      addressLine2: selectedCandidate.addressLine2 || null,
      addressCity: selectedCandidate.addressCity || null,
      addressState: selectedCandidate.addressState || null,
      addressPostalCode: selectedCandidate.addressPostalCode || null,
      addressCountry: selectedCandidate.addressCountry || null,
      degrees: normalizeDegrees(selectedCandidate.degrees),
    }, activeUsers); // 3rd arg: activeUsers for interviewer name resolution in hiring note
    setSaving(false);
    if (result.success) {
      setShowHireModal(false);
      // Replace any existing pending action with a "send_hire_letter" action
      // so Radar notifies the admin to send a confirmation/welcome letter.
      await updateCandidate(selectedCandidateId, {
        pendingAdminAction: 'send_hire_letter',
        pendingAdminActionAt: new Date().toISOString(),
        pendingAdminActionBy: currentUser?.authUserId || null,
      });
      const name = selectedCandidate?.candidateName || 'Candidate';
      if (result.noteWarning) {
        showToast(`${name} hired successfully! A notification has been sent to send a confirmation letter. (Note save failed.)`, 'error');
        console.error('[HiringPage] Note insert warning:', result.noteWarning);
      } else {
        showToast(`${name} hired successfully! A notification has been sent to send a confirmation letter.`);
      }
    } else {
      showToast(result.error || 'Hire failed', 'error');
    }
  };

  // ------------------------------------------------------------------
  // CANDIDATE ACTION DROPDOWN HANDLERS
  // ------------------------------------------------------------------
  // Request admin action: sets pending_admin_action on the candidate for radar pickup
  const requestAdminAction = async (actionType) => {
    if (!selectedCandidateId) return;
    const result = await updateCandidate(selectedCandidateId, {
      pendingAdminAction: actionType,
      pendingAdminActionAt: new Date().toISOString(),
      pendingAdminActionBy: currentUser?.authUserId || null,
    });
    if (result.success) {
      showToast(`${actionType === 'offer' ? 'Offer' : 'Hire'} request sent to admin`);
    } else {
      showToast(result.error || 'Failed to request admin action', 'error');
    }
  };

  // Clear pending admin action after admin completes it
  const clearPendingAdminAction = async (candidateId) => {
    if (!candidateId) return;
    await updateCandidate(candidateId, {
      pendingAdminAction: null,
      pendingAdminActionAt: null,
      pendingAdminActionBy: null,
    });
  };

  const handleCandidateAction = async (actionKey) => {
    setActionDropdownOpen(false);
    if (!selectedCandidate) return;
    switch (actionKey) {
      case 'hire':
        if (isAdmin) {
          handleOpenHireModal();
        } else {
          await requestAdminAction('hire');
        }
        break;
      case 'rejected':
        await handleArchiveToggle('Rejected');
        setEmailDraftAction({ eventType: 'hiring_rejection', archiveReason: 'Rejected' });
        break;
      case 'future_potential':
        await handleArchiveToggle('Future potential');
        setEmailDraftAction({ eventType: 'hiring_future_potential', archiveReason: 'Future potential' });
        break;
      case 'offer':
        if (isAdmin) {
          setEmailDraftAction({ eventType: 'hiring_offer' });
        } else {
          await requestAdminAction('offer');
        }
        break;
      case 'contact':
        setContactTemplateKey('hiring_contact');
        setEmailDraftAction({ eventType: 'hiring_contact', isContact: true });
        break;
      case 'move_to_current':
        if (selectedCandidate.hiredUserId) {
          await handleMoveHiredToCurrent();
        } else {
          await handleArchiveToggle(null);
        }
        break;
      default:
        break;
    }
  };

  // Build hiring-specific template vars for email drafts
  const buildHiringVars = useCallback(() => {
    if (!selectedCandidate) return {};
    return {
      addressee: selectedCandidate.candidateName || '',
      candidateName: selectedCandidate.candidateName || '',
      jobRole: selectedCandidate.jobRole || '',
      officeLocation: selectedCandidate.locationCity || 'Rotterdam / Mumbai',
    };
  }, [selectedCandidate]);

  // ══════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily: BRIO.font }}>
      {/* ─── HEADER BAR ─── */}
      <div style={{
        background: BRIO.white, borderBottom: `1px solid ${BRIO.border}`,
        padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '16px', borderRadius: 0,
      }}>
        <Users style={{ width: 20, height: 20, color: BRIO.gold, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 500, color: BRIO.black, fontFamily: BRIO.font, textTransform: 'lowercase' }}>hiring</h1>
          <div style={{ fontSize: '11px', color: BRIO.grey, marginTop: '2px' }}>candidate tracking and interview management</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
          {saving && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: BRIO.greyMuted, fontSize: '11px' }}>
              <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />saving…
            </div>
          )}
          {canWriteInCurrentMode && (
            <button onClick={() => { setNewCandidateForm({ candidateName: '', category: defaultNewCategory, jobRole: '' }); setShowNewCandidateModal(true); }}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: BRIO.gold, color: BRIO.white, border: 'none', borderRadius: 0, fontSize: '12px', fontWeight: 500, fontFamily: BRIO.font, cursor: 'pointer' }}>
              <Plus style={{ width: 13, height: 13 }} /> add candidate
            </button>
          )}
        </div>
      </div>

      {/* ─── CURRENT / ARCHIVED / HIRED TABS — supervisors and temp-access
            users see only current candidates ─── */}
      <div style={{ display: 'flex', gap: '2px', borderBottom: `1px solid ${BRIO.border}`, marginBottom: '12px' }}>
        {(currentOnlyView
          ? [{ id: 'current', label: 'current candidates' }]
          : [{ id: 'current', label: 'current candidates' }, { id: 'archived', label: 'archived candidates' }, { id: 'hired', label: 'hired candidates' }]
        ).map(t => (
          <button key={t.id} onClick={() => setViewMode(t.id)}
            style={{
              padding: '8px 16px', background: 'transparent', border: 'none',
              borderBottom: viewMode === t.id ? `2px solid ${BRIO.gold}` : '2px solid transparent',
              color: viewMode === t.id ? BRIO.black : BRIO.grey,
              fontSize: '12px', fontWeight: viewMode === t.id ? 500 : 300,
              cursor: 'pointer', fontFamily: BRIO.font, textTransform: 'lowercase', transition: 'all 0.15s',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── CATEGORY PILLS ─── */}
      <div style={{ marginBottom: '10px', overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: '6px', paddingBottom: '4px' }}>
          {CATEGORY_PILLS.map(cat => {
            const isAll = cat === ALL_CATEGORY;
            const count = listCandidates({ viewMode, category: isAll ? undefined : cat }).length;
            const active = selectedCategory === cat;
            return (
              <button key={cat} onClick={() => setSelectedCategory(cat)}
                style={{
                  flexShrink: 0, padding: '6px 14px', borderRadius: 0, fontSize: '12px',
                  fontWeight: active ? 500 : 400, fontFamily: BRIO.font, textTransform: 'lowercase',
                  background: active ? BRIO.goldLight : 'transparent',
                  color: active ? BRIO.black : BRIO.grey,
                  border: active ? `1px solid ${BRIO.goldMedium}` : `1px solid ${BRIO.border}`,
                  cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  marginRight: isAll ? '8px' : 0,
                }}>
                {cat}
                <span style={{ fontSize: '10px', padding: '1px 6px', background: active ? BRIO.goldMedium : BRIO.greyLight, color: active ? BRIO.goldHover : BRIO.greyMuted, borderRadius: '10px', fontWeight: 500 }}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══════════════ SPLIT LAYOUT: Left List + Right Detail ═══════════════ */}
      <div style={{ display: 'flex', gap: '0px', alignItems: 'stretch', minHeight: 'calc(100vh - 230px)', position: 'relative' }}>

        {/* ─── LEFT PANEL: Candidate List (collapsible + resizable) ─── */}
        <div ref={panelRef} style={{
          width: panelCollapsed ? '42px' : `${panelWidth}px`,
          minWidth: panelCollapsed ? '42px' : '220px',
          maxWidth: panelCollapsed ? '42px' : '520px',
          flexShrink: 0,
          background: BRIO.white, border: `1px solid ${BRIO.border}`, borderRadius: 0,
          display: 'flex', flexDirection: 'column',
          borderRight: `1px solid ${BRIO.border}`,
          transition: panelCollapsed ? 'width 0.2s ease' : 'none',
          overflow: 'hidden', position: 'relative',
        }}>
          {/* Collapse / Expand toggle */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: panelCollapsed ? 'center' : 'space-between',
            padding: panelCollapsed ? '8px 0' : '6px 12px 0',
            gap: '6px',
          }}>
            {!panelCollapsed && (
              <span style={{ fontSize: '10px', fontWeight: 500, color: BRIO.greyMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Candidates
              </span>
            )}
            <button
              onClick={() => setPanelCollapsed(prev => !prev)}
              title={panelCollapsed ? 'Expand panel' : 'Collapse panel'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, border: 'none', borderRadius: 0,
                background: 'transparent', cursor: 'pointer', color: BRIO.greyMuted,
                transition: 'background 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = BRIO.greyLight; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {panelCollapsed
                ? <PanelLeftOpen style={{ width: 14, height: 14 }} />
                : <PanelLeftClose style={{ width: 14, height: 14 }} />
              }
            </button>
          </div>

          {/* Collapsed state: vertical label + candidate count */}
          {panelCollapsed && (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'flex-start', paddingTop: '12px', gap: '12px',
            }}>
              <span style={{
                writingMode: 'vertical-rl', textOrientation: 'mixed',
                fontSize: '11px', fontWeight: 400, color: BRIO.grey,
                letterSpacing: '0.06em', transform: 'rotate(180deg)',
              }}>
                {processedCandidates.length} candidate{processedCandidates.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Expanded content */}
          {!panelCollapsed && (
            <>
          {/* Search bar */}
          <div style={{ padding: '6px 12px 0' }}>
            <div style={{ position: 'relative' }}>
              <Search style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: BRIO.greyMuted, pointerEvents: 'none' }} />
              <input
                type="text" value={listSearch} onChange={e => setListSearch(e.target.value)}
                placeholder="Search name or city…"
                style={{ ...INPUT_STYLE, paddingLeft: '28px', fontSize: '12px', height: '32px' }}
              />
              {listSearch && (
                <button onClick={() => setListSearch('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: BRIO.greyMuted, padding: '2px' }}>
                  <X style={{ width: 12, height: 12 }} />
                </button>
              )}
            </div>
          </div>

          {/* Filter chips */}
          <div style={{ padding: '8px 12px 0', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {[
              { key: 'all', label: 'All' },
              { key: 'recommended', label: '★ Rec' },
              { key: 'interviewed', label: 'Interviewed' },
              { key: 'no_interview', label: 'No Int.' },
              ...(isArchived ? [
                { key: 'Hired', label: 'Hired' },
                { key: 'Rejected', label: 'Rejected' },
                { key: 'Future potential', label: 'Future' },
              ] : []),
            ].map(f => {
              const active = listFilter === f.key;
              return (
                <button key={f.key} onClick={() => setListFilter(active ? 'all' : f.key)}
                  style={{
                    padding: '2px 7px', fontSize: '10px', borderRadius: '10px', lineHeight: '16px',
                    fontFamily: BRIO.font, fontWeight: active ? 500 : 400,
                    background: active ? BRIO.goldLight : 'transparent',
                    color: active ? BRIO.gold : BRIO.greyMuted,
                    border: `1px solid ${active ? BRIO.goldMedium : BRIO.border}`,
                    cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.12s',
                  }}>
                  {f.label}
                </button>
              );
            })}
          </div>

          {/* Sort + count */}
          <div style={{ padding: '6px 12px 8px', display: 'flex', alignItems: 'center', gap: '6px', borderBottom: `1px solid ${BRIO.border}` }}>
            <SlidersHorizontal style={{ width: 10, height: 10, color: BRIO.greyMuted, flexShrink: 0 }} />
            <select value={listSort} onChange={e => setListSort(e.target.value)}
              style={{ ...SELECT_STYLE, fontSize: '10px', padding: '2px 4px', flex: 1, color: BRIO.grey, height: '22px' }}>
              <option value="activity">Last Activity</option>
              <option value="name">Name A–Z</option>
              <option value="experience">Yrs Experience ↓</option>
              <option value="joining">Joining Date</option>
            </select>
            <span style={{ fontSize: '10px', color: BRIO.greyMuted, whiteSpace: 'nowrap' }}>
              {processedCandidates.length}
            </span>
          </div>

          {/* Scrollable candidate rows */}
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {loading && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px', color: BRIO.grey, gap: '6px' }}>
                <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: '11px' }}>Loading…</span>
              </div>
            )}

            {!loading && processedCandidates.length === 0 && (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: BRIO.greyMuted }}>
                <Briefcase style={{ width: 24, height: 24, margin: '0 auto 8px', opacity: 0.25 }} />
                <div style={{ fontSize: '11px', fontStyle: 'italic' }}>
                  {listSearch || listFilter !== 'all' ? 'No matches.' : `No ${viewMode} candidates.`}
                </div>
              </div>
            )}

            {!loading && visibleCandidates.map(c => {
              const active = selectedCandidateId === c.id;
              const ARCHIVE_COLORS = {
                'Hired':            { bg: 'rgba(132, 164, 140, 0.08)', text: 'var(--brio-green)' },
                'Rejected':         { bg: 'rgba(147, 73, 72, 0.08)', text: 'var(--brio-red)' },
                'Future potential':  { bg: 'rgba(236, 210, 136, 0.15)', text: 'var(--brio-orange)' },
              };
              return (
                <button key={c.id} onClick={() => setSelectedCandidateId(c.id)}
                  style={{
                    width: '100%', display: 'flex', flexDirection: 'column', gap: '2px',
                    padding: '9px 12px 9px 14px', border: 'none',
                    borderBottom: `1px solid ${BRIO.border}`,
                    borderLeft: active ? `3px solid ${BRIO.gold}` : '3px solid transparent',
                    background: active ? BRIO.goldLight : 'transparent',
                    cursor: 'pointer', fontFamily: BRIO.font, textAlign: 'left',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = BRIO.greyLight; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = active ? BRIO.goldLight : 'transparent'; }}>
                  {/* Row 1: Name + recommended */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span style={{
                      fontSize: '12px', fontWeight: active ? 500 : 400, color: BRIO.black,
                      flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {c.candidateName}
                    </span>
                    {c._isRecommended && (
                      <Star style={{ width: 11, height: 11, color: 'var(--brio-orange)', fill: 'var(--brio-orange)', flexShrink: 0 }} />
                    )}
                    {c._isUndecided && (
                      <Circle style={{ width: 11, height: 11, color: 'var(--brio-orange)', flexShrink: 0 }} />
                    )}
                    {c.pendingAdminAction && (
                      <span style={{ fontSize: '9px', fontWeight: 500, color: BRIO.gold, flexShrink: 0 }}>
                        {c.pendingAdminAction === 'offer' ? 'OFFER' : c.pendingAdminAction === 'send_hire_letter' ? 'LETTER' : 'HIRE'}
                      </span>
                    )}
                  </div>
                  {/* Row 2: Job Role · City · employed · interview count */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: BRIO.greyMuted, lineHeight: '14px' }}>
                    {/* Category only matters when the list is not already filtered to one */}
                    {isAllCategory && c.category && (
                      <>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '110px' }}>{c.category}</span>
                        {c.jobRole && <span>·</span>}
                      </>
                    )}
                    {c.jobRole && <span style={{ color: BRIO.gold, fontWeight: 500 }}>{c.jobRole}</span>}
                    {c.jobRole && (c.locationCity || c.employedStatus === 'employed') && <span>·</span>}
                    {c.locationCity && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px' }}>{c.locationCity}</span>}
                    {c.locationCity && c.employedStatus === 'employed' && <span>·</span>}
                    {c.employedStatus === 'employed' && <span style={{ color: 'var(--brio-green)' }}>employed</span>}
                    {c._interviewCount > 0 && (
                      <>
                        {(c.jobRole || c.locationCity || c.employedStatus === 'employed') && <span>·</span>}
                        <span>{c._interviewCount} int{c._interviewCount !== 1 ? 's' : ''}</span>
                      </>
                    )}
                  </div>
                  {/* Row 3: Last interview date + archive badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', lineHeight: '14px' }}>
                    {c._lastInterviewDate && (
                      <span style={{ color: BRIO.greyMuted }}>
                        last int: {new Date(c._lastInterviewDate + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
                      </span>
                    )}
                    {c.isArchived && c.archiveReason && (() => {
                      const ac = ARCHIVE_COLORS[c.archiveReason] || { bg: BRIO.greyLight, text: BRIO.grey };
                      return (
                        <span style={{
                          marginLeft: 'auto', padding: '0 5px', borderRadius: '6px',
                          fontSize: '9px', fontWeight: 500, background: ac.bg, color: ac.text,
                        }}>
                          {c.archiveReason}
                        </span>
                      );
                    })()}
                  </div>
                </button>
              );
            })}

            {/* Load more */}
            {hasMore && (
              <button onClick={() => setListShowCount(prev => prev + PAGE_SIZE)}
                style={{
                  width: '100%', padding: '10px', border: 'none',
                  background: BRIO.greyLight, color: BRIO.grey,
                  fontSize: '11px', fontFamily: BRIO.font, cursor: 'pointer', textAlign: 'center',
                }}>
                Show more ({processedCandidates.length - listShowCount} remaining)
              </button>
            )}
          </div>
            </>
          )}{/* end expanded content */}

          {/* Resize drag handle */}
          {!panelCollapsed && (
            <div
              onMouseDown={onResizeStart}
              style={{
                position: 'absolute', top: 0, right: -3, bottom: 0, width: 6,
                cursor: 'col-resize', zIndex: 10,
                background: 'transparent',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = BRIO.goldMedium; }}
              onMouseLeave={e => { if (!resizing.current) e.currentTarget.style.background = 'transparent'; }}
            />
          )}
        </div>

        {/* ─── RIGHT PANEL: Detail / Empty / Loading ─── */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Loading (only when no candidate selected yet) */}
          {loading && !selectedCandidate && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', color: BRIO.grey, gap: '8px' }}>
              <Loader2 style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '13px', fontFamily: BRIO.font }}>Loading hiring data…</span>
            </div>
          )}

          {/* Empty state — no candidates at all */}
          {!loading && filteredCandidates.length === 0 && (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: BRIO.greyMuted, border: `1px dashed ${BRIO.border}`, borderRadius: 0 }}>
              <Briefcase style={{ width: 32, height: 32, margin: '0 auto 12px', opacity: 0.3 }} />
              <div style={{ fontSize: '14px', fontWeight: 500, color: BRIO.black }}>No {viewMode} candidates{isAllCategory ? '' : ` in ${selectedCategory}`}</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>{canWriteInCurrentMode ? "Click '+ add candidate' to get started." : 'No candidates have been added yet.'}</div>
            </div>
          )}

          {/* No selection state (candidates exist but none selected) */}
          {!loading && filteredCandidates.length > 0 && !selectedCandidate && (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: BRIO.greyMuted, borderRadius: 0 }}>
              <Users style={{ width: 28, height: 28, margin: '0 auto 10px', opacity: 0.3 }} />
              <div style={{ fontSize: '13px' }}>Select a candidate from the list</div>
            </div>
          )}

      {/* ─── CANDIDATE DETAIL EDITOR ─── */}
      {selectedCandidate && editingCandidate && (
        <div style={{ background: BRIO.white, border: `1px solid ${BRIO.border}`, borderRadius: 0, borderLeft: 'none', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: `1px solid ${BRIO.border}`, background: BRIO.goldLight }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 500, color: BRIO.black, fontFamily: BRIO.font, textTransform: 'lowercase' }}>{selectedCandidate.candidateName}</h3>
              <div style={{ fontSize: '11px', color: BRIO.grey, marginTop: '2px' }}>{selectedCandidate.category}{selectedCandidate.jobRole ? ` · ${selectedCandidate.jobRole}` : ''} · added {new Date(selectedCandidate.createdAt).toLocaleDateString()}</div>
              {selectedCandidate.hiredUserId && (
                <div style={{ fontSize: '11px', color: 'var(--brio-green)', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <UserPlus style={{ width: 11, height: 11 }} /> Linked Employee ID: <strong>{selectedCandidate.hiredUserId}</strong>
                  {selectedCandidate.hiredAt && <span style={{ color: BRIO.grey }}> · hired {new Date(selectedCandidate.hiredAt).toLocaleDateString()}</span>}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {canDeleteInCurrentMode && (
                <button onClick={() => setConfirmDelete({ type: 'candidate', id: selectedCandidate.id, name: selectedCandidate.candidateName })}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', background: 'transparent', border: `1px solid ${BRIO.danger}33`, borderRadius: 0, color: BRIO.danger, fontSize: '11px', fontFamily: BRIO.font, cursor: 'pointer' }}
                  title="Delete candidate">
                  <Trash2 style={{ width: 12, height: 12 }} />delete
                </button>
              )}
              {/* ─── Actions Dropdown ─── */}
              {canWriteInCurrentMode && (
                <div ref={actionDropdownRef} style={{ position: 'relative' }}>
                  <button onClick={() => setActionDropdownOpen(prev => !prev)} disabled={saving}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', background: BRIO.white, border: `1px solid ${BRIO.border}`, borderRadius: 0, fontSize: '11px', fontFamily: BRIO.font, cursor: saving ? 'default' : 'pointer', color: BRIO.black, opacity: saving ? 0.5 : 1 }}>
                    actions <ChevronDown style={{ width: 11, height: 11, color: BRIO.grey }} />
                  </button>
                  {actionDropdownOpen && (
                    <div style={{
                      position: 'absolute', top: '100%', right: 0, marginTop: '2px', zIndex: 100,
                      background: BRIO.white, border: `1px solid ${BRIO.border}`, borderRadius: 0,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '180px',
                    }}>
                      {!selectedCandidate.isArchived && !selectedCandidate.hiredUserId && (isAdmin || isSupervisor) && (
                        <button onClick={() => handleCandidateAction('hire')}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', fontSize: '12px', fontFamily: BRIO.font, background: 'transparent', border: 'none', borderBottom: `1px solid ${BRIO.border}`, cursor: 'pointer', color: 'var(--brio-green)', textAlign: 'left' }}>
                          <UserPlus style={{ width: 13, height: 13 }} /> {isAdmin ? 'Hire Candidate' : 'Request Hire'}
                        </button>
                      )}
                      <button onClick={() => handleCandidateAction('offer')}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', fontSize: '12px', fontFamily: BRIO.font, background: 'transparent', border: 'none', borderBottom: `1px solid ${BRIO.border}`, cursor: 'pointer', color: BRIO.black, textAlign: 'left' }}>
                        <Mail style={{ width: 13, height: 13, color: BRIO.gold }} /> {isAdmin ? 'Offer Position' : 'Request Offer'}
                      </button>
                      <button onClick={() => handleCandidateAction('contact')}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', fontSize: '12px', fontFamily: BRIO.font, background: 'transparent', border: 'none', borderBottom: `1px solid ${BRIO.border}`, cursor: 'pointer', color: BRIO.black, textAlign: 'left' }}>
                        <Mail style={{ width: 13, height: 13, color: BRIO.grey }} /> Contact
                      </button>
                      {!selectedCandidate.isArchived && (
                        <>
                          <button onClick={() => handleCandidateAction('rejected')}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', fontSize: '12px', fontFamily: BRIO.font, background: 'transparent', border: 'none', borderBottom: `1px solid ${BRIO.border}`, cursor: 'pointer', color: 'var(--brio-red)', textAlign: 'left' }}>
                            <UserX style={{ width: 13, height: 13 }} /> Rejected
                          </button>
                          <button onClick={() => handleCandidateAction('future_potential')}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', fontSize: '12px', fontFamily: BRIO.font, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--brio-orange)', textAlign: 'left' }}>
                            <Star style={{ width: 13, height: 13 }} /> Future Potential
                          </button>
                        </>
                      )}
                      {(selectedCandidate.isArchived || selectedCandidate.hiredUserId) && (
                        <button onClick={() => handleCandidateAction('move_to_current')}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', fontSize: '12px', fontFamily: BRIO.font, background: 'transparent', border: 'none', borderTop: `1px solid ${BRIO.border}`, cursor: 'pointer', color: BRIO.grey, textAlign: 'left' }}>
                          <RotateCcw style={{ width: 13, height: 13 }} /> Move to Current
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {canWriteInCurrentMode && (
                <button onClick={handleSaveCandidate} disabled={saving}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: BRIO.gold, color: BRIO.white, border: 'none', borderRadius: 0, fontSize: '12px', fontWeight: 500, fontFamily: BRIO.font, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1, transition: 'all 0.2s' }}>
                  <Save style={{ width: 13, height: 13 }} />{saving ? 'saving…' : 'save changes'}
                </button>
              )}
            </div>
          </div>

          {/* Detail Fields */}
          <div style={{ padding: '4px 20px 20px' }}>
            <FieldGrid cols={3}>
              <Field label="Candidate Name">
                <input type="text" value={editingCandidate.candidateName || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, candidateName: e.target.value }))} disabled={!canWriteInCurrentMode} style={INPUT_STYLE} placeholder="Full name" />
              </Field>
              <Field label="Category">
                <select value={editingCandidate.category || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, category: e.target.value }))} disabled={!canWriteInCurrentMode} style={SELECT_STYLE}>
                  {HIRING_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </Field>
              <Field label="Job Role *">
                <select value={editingCandidate.jobRole || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, jobRole: e.target.value }))} disabled={!canWriteInCurrentMode} style={{
                  ...SELECT_STYLE,
                  borderColor: !editingCandidate.jobRole && canWriteInCurrentMode ? 'var(--brio-yellow)' : undefined,
                }}>
                  <option value="">— Select Job Role —</option>
                  {BRIO_JOB_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {!editingCandidate.jobRole && canWriteInCurrentMode && (
                  <span style={{ fontSize: '10px', color: 'var(--brio-orange)', marginTop: '1px' }}>Required</span>
                )}
              </Field>
              <Field label="Employment Status">
                <select value={editingCandidate.employedStatus || 'not_employed'} onChange={e => setEditingCandidate(prev => ({ ...prev, employedStatus: e.target.value }))} disabled={!canWriteInCurrentMode} style={SELECT_STYLE}>
                  <option value="employed">Employed</option>
                  <option value="not_employed">Not Employed</option>
                </select>
              </Field>
              <Field label="Years Experience">
                <input type="number" value={editingCandidate.yearsExperience ?? ''} onChange={e => setEditingCandidate(prev => ({ ...prev, yearsExperience: e.target.value === '' ? null : parseFloat(e.target.value) }))} disabled={!canWriteInCurrentMode} style={{ ...INPUT_STYLE, maxWidth: '140px' }} placeholder="0" min="0" step="0.5" />
              </Field>
              <Field label="Possible Joining Date">
                <BrioDatePicker value={editingCandidate.possibleJoiningDate || ''} onChange={v => setEditingCandidate(prev => ({ ...prev, possibleJoiningDate: v }))} disabled={!canWriteInCurrentMode} />
              </Field>
              <Field label="Portfolio URL">
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="url" value={editingCandidate.portfolioUrl || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, portfolioUrl: e.target.value }))} disabled={!canWriteInCurrentMode} style={{ ...INPUT_STYLE, flex: 1 }} placeholder="https://..." />
                  {editingCandidate.portfolioUrl && (
                    <>
                      <button type="button" onClick={() => navigator.clipboard.writeText(editingCandidate.portfolioUrl)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, background: 'transparent', border: `1px solid ${BRIO.border}`, borderRadius: 0, cursor: 'pointer', color: BRIO.grey, flexShrink: 0 }} title="Copy URL">
                        <Copy style={{ width: 14, height: 14 }} />
                      </button>
                      <a href={editingCandidate.portfolioUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, background: 'transparent', border: `1px solid ${BRIO.border}`, borderRadius: 0, color: 'var(--brio-gold)', flexShrink: 0 }} title="Open in new tab">
                        <ExternalLink style={{ width: 14, height: 14 }} />
                      </a>
                    </>
                  )}
                </div>
              </Field>
              <Field label="Location City">
                <input type="text" value={editingCandidate.locationCity || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, locationCity: e.target.value }))} disabled={!canWriteInCurrentMode} style={INPUT_STYLE} placeholder="City" />
              </Field>
            </FieldGrid>

            {/* Contact */}
            <div style={SECTION_LABEL_STYLE}>Contact</div>
            <FieldGrid cols={3}>
              <Field label="Email">
                <input type="email" value={editingCandidate.email || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, email: e.target.value }))} disabled={!canWriteInCurrentMode} style={INPUT_STYLE} placeholder="candidate@email.com" />
              </Field>
              <Field label="Mobile Phone">
                <input type="tel" value={editingCandidate.phoneMobile || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, phoneMobile: e.target.value }))} disabled={!canWriteInCurrentMode} style={INPUT_STYLE} placeholder="+91 …" />
              </Field>
              <Field label="WhatsApp">
                <input type="tel" value={editingCandidate.phoneWhatsapp || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, phoneWhatsapp: e.target.value }))} disabled={!canWriteInCurrentMode} style={INPUT_STYLE} placeholder="+91 …" />
              </Field>
              <Field label={editingCandidate.phoneCustomLabel || 'Custom Phone'}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="text" value={editingCandidate.phoneCustomLabel || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, phoneCustomLabel: e.target.value }))} disabled={!canWriteInCurrentMode} style={{ ...INPUT_STYLE, maxWidth: '90px' }} placeholder="Label" />
                  <input type="tel" value={editingCandidate.phoneCustomValue || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, phoneCustomValue: e.target.value }))} disabled={!canWriteInCurrentMode} style={{ ...INPUT_STYLE, flex: 1 }} placeholder="Number" />
                </div>
              </Field>
            </FieldGrid>

            {/* Address */}
            <div style={SECTION_LABEL_STYLE}>Address</div>
            <FieldGrid cols={3}>
              <Field label="Address Line 1" span2>
                <input type="text" value={editingCandidate.addressLine1 || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, addressLine1: e.target.value }))} disabled={!canWriteInCurrentMode} style={INPUT_STYLE} placeholder="Street address" />
              </Field>
              <Field label="Address Line 2">
                <input type="text" value={editingCandidate.addressLine2 || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, addressLine2: e.target.value }))} disabled={!canWriteInCurrentMode} style={INPUT_STYLE} placeholder="Apt, suite, etc." />
              </Field>
              <Field label="City">
                <input type="text" value={editingCandidate.addressCity || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, addressCity: e.target.value }))} disabled={!canWriteInCurrentMode} style={INPUT_STYLE} placeholder="City" />
              </Field>
              <Field label="State / Province">
                <input type="text" value={editingCandidate.addressState || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, addressState: e.target.value }))} disabled={!canWriteInCurrentMode} style={INPUT_STYLE} placeholder="State" />
              </Field>
              <Field label="Postal Code">
                <input type="text" value={editingCandidate.addressPostalCode || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, addressPostalCode: e.target.value }))} disabled={!canWriteInCurrentMode} style={INPUT_STYLE} placeholder="Postal code" />
              </Field>
              <Field label="Country">
                <input type="text" value={editingCandidate.addressCountry || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, addressCountry: e.target.value }))} disabled={!canWriteInCurrentMode} style={INPUT_STYLE} placeholder="Country" />
              </Field>
            </FieldGrid>

            <Field label="Notes">
              <AutoResizeTextarea value={editingCandidate.notes || ''} onChange={e => setEditingCandidate(prev => ({ ...prev, notes: e.target.value }))} disabled={!canWriteInCurrentMode} minRows={2} placeholder="General notes..." />
            </Field>

            <div style={{ marginTop: '16px' }}>
              <label style={LABEL_STYLE}>Past Employment</label>
              <PastEmploymentEditor items={editingCandidate.pastEmployment || []} onChange={(items) => setEditingCandidate(prev => ({ ...prev, pastEmployment: items }))} disabled={!canWriteInCurrentMode} />
            </div>

            {/* Degrees / Education */}
            <div style={SECTION_LABEL_STYLE}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <GraduationCap style={{ width: 13, height: 13, color: BRIO.grey }} />
                <span>Degrees / Education</span>
              </div>
            </div>
            <div style={{ fontSize: '10px', color: BRIO.greyMuted, marginBottom: '8px', display: 'grid', gridTemplateColumns: '120px 1fr 110px 80px 1fr auto', gap: '6px', fontFamily: BRIO.font, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <span>Type</span><span>Institution</span><span>Graduation</span><span>Duration</span><span>Notes</span><span style={{ width: 16 }} />
            </div>
            <DegreesEditor items={editingCandidate.degrees || []} onChange={(items) => setEditingCandidate(prev => ({ ...prev, degrees: items }))} disabled={!canWriteInCurrentMode} />

            {/* Archived Status indicator */}
            {selectedCandidate.isArchived && (
              <div style={{ marginTop: '20px', padding: '14px 16px', background: BRIO.greyLight, borderRadius: 0, border: `1px solid ${BRIO.border}` }}>
                <span style={{ fontSize: '12px', color: BRIO.grey, fontFamily: BRIO.font }}>Archived — Reason: <strong style={{ color: BRIO.black }}>{selectedCandidate.archiveReason || 'N/A'}</strong></span>
              </div>
            )}

            {/* Interviews Section */}
            <div style={SECTION_LABEL_STYLE}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Interviews ({candidateInterviews.length})</span>
                {canWriteInCurrentMode && (
                  <button onClick={handleAddInterview} disabled={saving}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: BRIO.goldLight, border: `1px solid ${BRIO.goldMedium}`, borderRadius: 0, color: BRIO.gold, fontSize: '11px', fontFamily: BRIO.font, cursor: 'pointer', fontWeight: 400 }}>
                    <Plus style={{ width: 12, height: 12 }} /> add interview
                  </button>
                )}
              </div>
            </div>

            {candidateInterviews.length === 0 && (
              <div style={{ padding: '16px', border: `1px dashed ${BRIO.border}`, borderRadius: 0, textAlign: 'center', color: BRIO.greyMuted, fontSize: '12px' }}>No interviews recorded yet</div>
            )}

            {candidateInterviews.map(interview => (
              <InterviewPanel
                key={interview.id} interview={interview}
                isExpanded={expandedInterviews[interview.id] ?? false}
                onToggle={() => setExpandedInterviews(prev => ({ ...prev, [interview.id]: !prev[interview.id] }))}
                onUpdate={(updates) => handleUpdateInterview(interview.id, updates)}
                onDelete={() => setConfirmDelete({ type: 'interview', id: interview.id, name: `Interview ${interview.interviewIndex}` })}
                canWrite={canWriteInCurrentMode} canDelete={canWriteInCurrentMode}
                activeUsers={activeUsers} activeProjects={activeProjects} saving={saving}
              />
            ))}
          </div>
        </div>
      )}

        </div>{/* end right panel */}
      </div>{/* end split layout */}

      {/* ─── NEW CANDIDATE MODAL ─── */}
      {showNewCandidateModal && (
        <ModalOverlay title="add new candidate" onClose={() => setShowNewCandidateModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Field label="Candidate Name *">
              <input type="text" value={newCandidateForm.candidateName} onChange={e => setNewCandidateForm(prev => ({ ...prev, candidateName: e.target.value }))} style={INPUT_STYLE} placeholder="Full name" autoFocus />
            </Field>
            <Field label="Category">
              <select value={newCandidateForm.category} onChange={e => setNewCandidateForm(prev => ({ ...prev, category: e.target.value }))} style={SELECT_STYLE}>
                {HIRING_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </Field>
            <Field label="Job Role">
              <select value={newCandidateForm.jobRole || ''} onChange={e => setNewCandidateForm(prev => ({ ...prev, jobRole: e.target.value }))} style={SELECT_STYLE}>
                <option value="">— Select Job Role —</option>
                {BRIO_JOB_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '8px' }}>
              <button onClick={() => setShowNewCandidateModal(false)}
                style={{ padding: '6px 14px', background: 'transparent', border: `1px solid ${BRIO.border}`, borderRadius: 0, color: BRIO.grey, fontSize: '12px', fontFamily: BRIO.font, cursor: 'pointer' }}>cancel</button>
              <button onClick={handleCreateCandidate} disabled={saving || !newCandidateForm.candidateName.trim()}
                style={{ padding: '6px 14px', background: BRIO.gold, color: BRIO.white, border: 'none', borderRadius: 0, fontSize: '12px', fontWeight: 500, fontFamily: BRIO.font, cursor: (saving || !newCandidateForm.candidateName.trim()) ? 'default' : 'pointer', opacity: (saving || !newCandidateForm.candidateName.trim()) ? 0.5 : 1 }}>
                {saving ? 'creating…' : 'create candidate'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ─── DELETE CONFIRMATION MODAL ─── */}
      {confirmDelete && (
        <ModalOverlay title={`delete ${confirmDelete.type === 'candidate' ? 'candidate' : 'interview'}`} onClose={() => setConfirmDelete(null)}>
          <p style={{ fontSize: '13px', color: BRIO.grey, fontFamily: BRIO.font, margin: '0 0 16px' }}>
            Are you sure you want to delete <strong style={{ color: BRIO.black }}>{confirmDelete.name}</strong>?
            {confirmDelete.type === 'candidate' && ' This will also delete all associated interviews.'}
            {' '}This action cannot be undone.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button onClick={() => setConfirmDelete(null)}
              style={{ padding: '6px 14px', background: 'transparent', border: `1px solid ${BRIO.border}`, borderRadius: 0, color: BRIO.grey, fontSize: '12px', fontFamily: BRIO.font, cursor: 'pointer' }}>cancel</button>
            <button onClick={handleConfirmDelete} disabled={saving}
              style={{ padding: '6px 14px', background: BRIO.danger, color: BRIO.white, border: 'none', borderRadius: 0, fontSize: '12px', fontWeight: 500, fontFamily: BRIO.font, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'deleting…' : 'delete'}
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* ─── HIRE CANDIDATE MODAL (admin only) ─── */}
      {showHireModal && selectedCandidate && (
        <ModalOverlay title={`hire ${selectedCandidate.candidateName}`} onClose={() => setShowHireModal(false)}>
          <div style={{ fontSize: '12px', color: BRIO.grey, marginBottom: '14px', fontFamily: BRIO.font }}>
            This will create a new team member, populate their profile with all hiring data, and archive the candidate as "Hired".
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Field label="Work Email *">
              <input type="email" value={hireForm.workEmail} onChange={e => setHireForm(prev => ({ ...prev, workEmail: e.target.value }))} style={INPUT_STYLE} placeholder="name@bfroelicharchitects.com" autoFocus />
            </Field>
            <Field label="Team">
              <select value={hireForm.teamId} onChange={e => setHireForm(prev => ({ ...prev, teamId: e.target.value }))} style={SELECT_STYLE}>
                <option value="">Select team…</option>
                {(activeTeams || []).map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
              </select>
              {(!activeTeams || activeTeams.length === 0) && (
                <div style={{ fontSize: '11px', color: BRIO.danger, marginTop: '4px', fontFamily: BRIO.font }}>No teams found — create teams first</div>
              )}
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Field label="Job Role *">
                <select value={hireForm.jobRole} onChange={e => setHireForm(prev => ({ ...prev, jobRole: e.target.value }))} style={SELECT_STYLE}>
                  <option value="">Select role…</option>
                  {BRIO_JOB_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Job Level *">
                <input type="number" value={hireForm.jobLevel} onChange={e => setHireForm(prev => ({ ...prev, jobLevel: e.target.value }))} style={INPUT_STYLE} placeholder="1" min="0" max="20" />
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <Field label="Join Date *">
                <BrioDatePicker value={hireForm.joinDate} onChange={v => setHireForm(prev => ({ ...prev, joinDate: v }))} />
              </Field>
              <Field label="Office Location">
                <select value={hireForm.officeLocation} onChange={e => setHireForm(prev => ({ ...prev, officeLocation: e.target.value }))} style={SELECT_STYLE}>
                  <option value="NL">NL</option>
                  <option value="IN">IN</option>
                </select>
              </Field>
              <Field label="Contracted Hours (per month)">
                <input type="number" value={hireForm.contractedHours} onChange={e => setHireForm(prev => ({ ...prev, contractedHours: e.target.value }))} style={INPUT_STYLE} placeholder="176" step="0.5" />
                <div style={{ fontSize: '10px', color: BRIO.greyMuted, marginTop: '2px', fontFamily: BRIO.font }}>Default: 176 / month</div>
              </Field>
            </div>
            {(hireForm.officeLocation || 'NL') === 'NL' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Field label="NL Schaal (CAO) *">
                  <select value={hireForm.nlSchaal} onChange={e => setHireForm(prev => ({ ...prev, nlSchaal: e.target.value }))} style={SELECT_STYLE}>
                    <option value="">Select schaal…</option>
                    {['1','2','3','4','5','6','7','8','9','10'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <div style={{ fontSize: '10px', color: BRIO.greyMuted, marginTop: '2px', fontFamily: BRIO.font }}>CAO art. 20 salary scale</div>
                </Field>
                <Field label="NL Trede *">
                  <select value={hireForm.nlTrede} onChange={e => setHireForm(prev => ({ ...prev, nlTrede: e.target.value }))} style={SELECT_STYLE}>
                    <option value="">Select trede…</option>
                    {['0','1','2','3','4','5','6','7','8','9'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <div style={{ fontSize: '10px', color: BRIO.greyMuted, marginTop: '2px', fontFamily: BRIO.font }}>Annual salary step (0-9)</div>
                </Field>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '8px', borderTop: `1px solid ${BRIO.border}` }}>
              <button onClick={() => setShowHireModal(false)}
                style={{ padding: '6px 14px', background: 'transparent', border: `1px solid ${BRIO.border}`, borderRadius: 0, color: BRIO.grey, fontSize: '12px', fontFamily: BRIO.font, cursor: 'pointer' }}>cancel</button>
              <button onClick={handleConfirmHire} disabled={saving}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: 'var(--brio-green)', color: BRIO.white, border: 'none', borderRadius: 0, fontSize: '12px', fontWeight: 500, fontFamily: BRIO.font, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                <UserPlus style={{ width: 13, height: 13 }} />{saving ? 'hiring…' : 'confirm hire'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ─── HIRING EMAIL DRAFT MODAL ─── */}
      {emailDraftAction && selectedCandidate && (
        <HiringEmailDraftModal
          candidate={selectedCandidate}
          emailDraftAction={emailDraftAction}
          contactTemplateKey={contactTemplateKey}
          onContactTemplateChange={setContactTemplateKey}
          customEmailTemplates={customEmailTemplates}
          buildHiringVars={buildHiringVars}
          onClose={() => setEmailDraftAction(null)}
          onSend={(toEmail, subject, body, eventType) => {
            const mailtoUrl = buildMailtoUrl({ to: [toEmail], subject, body });
            logEmail({ to: [toEmail], subject, eventType, templateUsed: eventType });
            window.open(mailtoUrl, '_blank');
            // Clear pending admin action if this was an offer or hire-letter email
            if (eventType === 'hiring_offer' && (selectedCandidate?.pendingAdminAction === 'offer' || selectedCandidate?.pendingAdminAction === 'send_hire_letter')) {
              clearPendingAdminAction(selectedCandidateId);
            }
            showToast('Email draft opened in mail client');
            setEmailDraftAction(null);
          }}
          showToast={showToast}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}


// ============================================================================
// INTERVIEW PANEL — Drawer-style collapsible (TeamMemberFullScreen pattern)
// ============================================================================
function InterviewPanel({ interview, isExpanded, onToggle, onUpdate, onDelete, canWrite, canDelete: canDeleteProp, activeUsers, activeProjects, saving }) {
  const [draft, setDraft] = useState({ ...interview });
  useEffect(() => { setDraft({ ...interview }); }, [interview]);

  const handleFieldChange = (field, value) => { setDraft(prev => ({ ...prev, [field]: value })); };
  const handleSave = () => { onUpdate(draft); };
  const handleProficiencyChange = (key, value) => {
    const current = draft.softwareProficiency || {};
    const updated = { ...current, [key]: value };
    if (value === null) delete updated[key];
    handleFieldChange('softwareProficiency', updated);
  };

  return (
    <div style={{ borderBottom: `1px solid ${BRIO.border}`, background: BRIO.white, marginBottom: '2px' }}>
      {/* Collapsible Header — Drawer pattern */}
      <button onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
          padding: '12px 20px', background: isExpanded ? BRIO.goldLight : 'transparent',
          border: 'none', cursor: 'pointer', fontFamily: BRIO.font,
          fontSize: '13px', fontWeight: isExpanded ? 500 : 400,
          color: BRIO.black, textTransform: 'lowercase', transition: 'background 0.15s', textAlign: 'left',
        }}>
        {isExpanded
          ? <ChevronDown style={{ width: 14, height: 14, color: BRIO.grey }} />
          : <ChevronRight style={{ width: 14, height: 14, color: BRIO.grey }} />}
        <span style={{ flex: 1 }}>
          interview {interview.interviewIndex}
          {interview.interviewDate && <span style={{ marginLeft: '8px', fontSize: '11px', color: BRIO.greyMuted, fontWeight: 400 }}>{interview.interviewDate}</span>}
        </span>
        {interview.recommended && (
          <span style={{ fontSize: '10px', padding: '2px 7px', background: 'rgba(132, 164, 140, 0.08)', color: 'var(--brio-green)', borderRadius: '10px', fontWeight: 500, border: '1px solid #86efac' }}>recommended</span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} onClick={e => e.stopPropagation()}>
          {canDeleteProp && (
            <button onClick={onDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO.greyMuted, padding: '4px' }} title="Delete interview">
              <Trash2 style={{ width: 13, height: 13 }} />
            </button>
          )}
        </div>
      </button>

      {isExpanded && (
        <div style={{ padding: '4px 20px 20px' }}>
          <FieldGrid cols={3}>
            <Field label="Interview Date">
              <BrioDatePicker value={draft.interviewDate || ''} onChange={v => handleFieldChange('interviewDate', v)} disabled={!canWrite} />
            </Field>
            <Field label="Interview Taken By">
              {(() => {
                const uuidUsers = activeUsers
                  .filter(u => u.isActive !== false)
                  .map(u => ({ u, uid: getUserUuid(u) }))
                  .filter(x => x.uid);
                if (uuidUsers.length === 0) {
                  return <div style={{ fontSize: '11px', color: BRIO.danger, padding: '6px 0' }}>No valid user UUIDs found; cannot assign interviewer.</div>;
                }
                return (
                  <select value={draft.interviewTakenBy != null && isUuid(String(draft.interviewTakenBy)) ? String(draft.interviewTakenBy) : ''} onChange={e => { const val = e.target.value; handleFieldChange('interviewTakenBy', val || null); }} disabled={!canWrite} style={SELECT_STYLE}>
                    <option value="">Select interviewer…</option>
                    {uuidUsers.map(({u, uid}) => <option key={uid} value={uid}>{u.name}</option>)}
                  </select>
                );
              })()}
            </Field>
            <Field label="Recommended">
              <button type="button" onClick={() => {
                // Cycle: false → null (?) → true → false
                const next = draft.recommended === false ? null : draft.recommended === null ? true : false;
                handleFieldChange('recommended', next);
              }} disabled={!canWrite}
                style={{
                  padding: '6px 14px', borderRadius: 0, fontSize: '12px', fontWeight: 500, fontFamily: BRIO.font,
                  background: draft.recommended === true ? 'rgba(132, 164, 140, 0.08)' : draft.recommended === null ? 'rgba(159, 144, 42, 0.08)' : BRIO.white,
                  color: draft.recommended === true ? 'var(--brio-green)' : draft.recommended === null ? 'var(--brio-gold)' : BRIO.grey,
                  border: `1px solid ${draft.recommended === true ? '#86efac' : draft.recommended === null ? 'var(--brio-gold)' : BRIO.border}`,
                  cursor: canWrite ? 'pointer' : 'not-allowed', opacity: canWrite ? 1 : 0.5, transition: 'all 0.15s',
                }}>
                {draft.recommended === true ? '✓ yes' : draft.recommended === null ? '? undecided' : '✕ no'}
              </button>
            </Field>
            <Field label="Salary Expectations (monthly)">
              <input type="number" value={draft.salaryExpectationsMonthly ?? ''} onChange={e => handleFieldChange('salaryExpectationsMonthly', e.target.value === '' ? null : parseFloat(e.target.value))} disabled={!canWrite} style={INPUT_STYLE} placeholder="0" />
            </Field>
            <Field label="Current Salary (monthly)">
              <input type="number" value={draft.currentSalaryMonthly ?? ''} onChange={e => handleFieldChange('currentSalaryMonthly', e.target.value === '' ? null : parseFloat(e.target.value))} disabled={!canWrite} style={INPUT_STYLE} placeholder="0" />
            </Field>
            <Field label="Future Plans">
              <input type="text" value={draft.futurePlans || ''} onChange={e => handleFieldChange('futurePlans', e.target.value)} disabled={!canWrite} style={INPUT_STYLE} placeholder="Career goals..." />
            </Field>
          </FieldGrid>

          <Field label="Overall Comments">
            <AutoResizeTextarea value={draft.overallComments || ''} onChange={e => handleFieldChange('overallComments', e.target.value)} disabled={!canWrite} minRows={3} placeholder="General interview notes..." />
          </Field>

          <div style={{ ...SECTION_LABEL_STYLE, marginTop: '16px' }}>Software Proficiency</div>
          <div style={{ background: BRIO.greyLight, borderRadius: 0, padding: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
              {SOFTWARE_PROFICIENCY_KEYS.map(({ key, label }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: BRIO.white, borderRadius: 0, border: `1px solid ${BRIO.border}` }}>
                  <span style={{ fontSize: '11px', color: BRIO.grey, marginRight: '8px' }}>{label}</span>
                  <ProficiencyRating value={(draft.softwareProficiency || {})[key] || null} onChange={(val) => handleProficiencyChange(key, val)} disabled={!canWrite} />
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', background: BRIO.white, borderRadius: 0, border: `1px solid ${BRIO.border}`, gridColumn: 'span 3' }}>
                <input type="text" value={(draft.softwareProficiency || {}).others_text || ''} onChange={e => handleProficiencyChange('others_text', e.target.value || null)} disabled={!canWrite} placeholder="Other software..." style={{ ...INPUT_STYLE, flex: 1, padding: '4px 8px', fontSize: '12px' }} />
                <ProficiencyRating value={(draft.softwareProficiency || {}).others_level || null} onChange={(val) => handleProficiencyChange('others_level', val)} disabled={!canWrite} />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '16px' }}>
            <Field label="Suitable Projects">
              <ProjectMultiSelect selectedIds={draft.suitableProjectIds || []} projects={activeProjects} onChange={(ids) => handleFieldChange('suitableProjectIds', ids)} disabled={!canWrite} />
            </Field>
          </div>

          <div style={{ marginTop: '14px' }}>
            <Field label="Final Verdict Notes">
              <AutoResizeTextarea value={draft.finalVerdictNotes || ''} onChange={e => handleFieldChange('finalVerdictNotes', e.target.value)} disabled={!canWrite} minRows={2} placeholder="Final assessment..." />
            </Field>
          </div>

          {canWrite && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={handleSave} disabled={saving}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: BRIO.gold, color: BRIO.white, border: 'none', borderRadius: 0, fontSize: '12px', fontWeight: 500, fontFamily: BRIO.font, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1, transition: 'all 0.2s' }}>
                <Save style={{ width: 13, height: 13 }} />{saving ? 'saving…' : 'save interview'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// HIRING EMAIL DRAFT MODAL — lightweight email composer for candidate actions
// ============================================================================
function HiringEmailDraftModal({
  candidate,
  emailDraftAction,
  contactTemplateKey,
  onContactTemplateChange,
  customEmailTemplates,
  buildHiringVars,
  onClose,
  onSend,
  showToast,
}) {
  const hiringVars = buildHiringVars();
  const eventType = emailDraftAction.isContact ? contactTemplateKey : emailDraftAction.eventType;
  const rendered = renderEmailTemplate(eventType, hiringVars, customEmailTemplates);
  const hiringTemplateOptions = getHiringTemplateTypes();

  const [toEmail, setToEmail] = useState(candidate.email || '');
  const [subject, setSubject] = useState(rendered.subject);
  const [body, setBody] = useState(rendered.body);

  // Re-sync subject/body when template changes (Contact selector)
  const prevEventType = useRef(eventType);
  useEffect(() => {
    if (prevEventType.current !== eventType) {
      const fresh = renderEmailTemplate(eventType, hiringVars, customEmailTemplates);
      setSubject(fresh.subject);
      setBody(fresh.body);
      prevEventType.current = eventType;
    }
  }, [eventType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = () => {
    if (!toEmail.trim()) {
      showToast('Recipient email is required', 'error');
      return;
    }
    onSend(toEmail.trim(), subject, body, eventType);
  };

  return (
    <BrioModal title={`email draft — ${candidate.candidateName}`} onClose={onClose} width="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {emailDraftAction.isContact && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, color: 'var(--brio-grey)', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: "'Raleway', sans-serif" }}>Template</label>
            <select value={contactTemplateKey} onChange={e => onContactTemplateChange(e.target.value)}
              style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--brio-border)', borderRadius: 0, fontSize: '13px', lineHeight: '1.4', background: 'var(--brio-white)', color: 'var(--brio-black)', boxSizing: 'border-box', fontFamily: "'Raleway', sans-serif", appearance: 'auto' }}>
              {hiringTemplateOptions.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>
        )}
        {emailDraftAction.archiveReason && (
          <div style={{ padding: '8px 12px', background: 'rgba(236, 210, 136, 0.15)', border: '1px solid rgba(159, 144, 42, 0.2)', borderRadius: 0, fontSize: '11px', color: 'var(--brio-grey)', fontFamily: "'Raleway', sans-serif" }}>
            Candidate archived as <strong>{emailDraftAction.archiveReason}</strong>. Draft an email to notify them.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <label style={{ fontSize: '11px', fontWeight: 500, color: 'var(--brio-grey)', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: "'Raleway', sans-serif" }}>To</label>
          <input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)} placeholder="candidate@email.com" autoFocus
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--brio-border)', borderRadius: 0, fontSize: '13px', lineHeight: '1.4', background: 'var(--brio-white)', color: 'var(--brio-black)', boxSizing: 'border-box', fontFamily: "'Raleway', sans-serif" }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <label style={{ fontSize: '11px', fontWeight: 500, color: 'var(--brio-grey)', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: "'Raleway', sans-serif" }}>Subject</label>
          <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--brio-border)', borderRadius: 0, fontSize: '13px', lineHeight: '1.4', background: 'var(--brio-white)', color: 'var(--brio-black)', boxSizing: 'border-box', fontFamily: "'Raleway', sans-serif" }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <label style={{ fontSize: '11px', fontWeight: 500, color: 'var(--brio-grey)', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: "'Raleway', sans-serif" }}>Body</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={10}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--brio-border)', borderRadius: 0, fontSize: '13px', lineHeight: '1.5', background: 'var(--brio-white)', color: 'var(--brio-black)', boxSizing: 'border-box', fontFamily: "'Raleway', sans-serif", resize: 'vertical', minHeight: '180px' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '8px', borderTop: '1px solid var(--brio-border)' }}>
          <button onClick={onClose}
            style={{ padding: '6px 14px', background: 'transparent', border: '1px solid var(--brio-border)', borderRadius: 0, color: 'var(--brio-grey)', fontSize: '12px', fontFamily: "'Raleway', sans-serif", cursor: 'pointer' }}>
            cancel
          </button>
          <button onClick={handleSend}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 14px', background: 'var(--brio-gold)', color: 'var(--brio-white)', border: 'none', borderRadius: 0, fontSize: '12px', fontWeight: 500, fontFamily: "'Raleway', sans-serif", cursor: 'pointer' }}>
            <Mail style={{ width: 13, height: 13 }} /> open in mail client
          </button>
        </div>
      </div>
    </BrioModal>
  );
}
